import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import firebaseConfig from '../../firebase-applet-config.json';

// Reuse or initialize the app
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
export const auth = getAuth(app);

const provider = new GoogleAuthProvider();
// Required Google Drive Scope
provider.addScope('https://www.googleapis.com/auth/drive.file');
provider.addScope('https://www.googleapis.com/auth/gmail.send');

let isSigningIn = false;
let cachedAccessToken: string | null = null;

// Initialize auth state listener
export const initAuth = (
  onAuthSuccess?: (user: User, token: string) => void,
  onAuthFailure?: () => void
) => {
  return onAuthStateChanged(auth, async (user: User | null) => {
    if (user) {
      if (cachedAccessToken) {
        if (onAuthSuccess) onAuthSuccess(user, cachedAccessToken);
      } else if (!isSigningIn) {
        // Token might need to be re-fetched. Since we do popup auth,
        // we'll ask the user to manually click sign in if cachedAccessToken is not present yet.
        if (onAuthFailure) onAuthFailure();
      }
    } else {
      cachedAccessToken = null;
      if (onAuthFailure) onAuthFailure();
    }
  });
};

// Check if we are currently logged in with a cached token
export const getCachedToken = (): string | null => {
  return cachedAccessToken;
};

// Standard Sign-in helper
export const googleSignIn = async (): Promise<{ user: User; accessToken: string } | null> => {
  try {
    isSigningIn = true;
    const result = await signInWithPopup(auth, provider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    if (!credential?.accessToken) {
      throw new Error('Failed to retrieve access token from Google sign in credential.');
    }
    cachedAccessToken = credential.accessToken;
    return { user: result.user, accessToken: cachedAccessToken };
  } catch (error) {
    console.error('Sign in error:', error);
    throw error;
  } finally {
    isSigningIn = false;
  }
};

// Logout helper
export const googleSignOut = async () => {
  await auth.signOut();
  cachedAccessToken = null;
};

// Get or Create Directory Folder in Google Drive
export const getOrCreateFolder = async (accessToken: string, folderName = 'cloudairambo_Invoices'): Promise<string> => {
  try {
    const q = `name='${folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id,name)`,
      {
        headers: { Authorization: `Bearer ${accessToken}` }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to query folder: ${await response.text()}`);
    }

    const data = await response.json();
    const files = data.files || [];

    if (files.length > 0) {
      return files[0].id;
    }

    // Create folder
    const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: folderName,
        mimeType: 'application/vnd.google-apps.folder'
      })
    });

    if (!createRes.ok) {
      throw new Error(`Failed to create folder: ${await createRes.text()}`);
    }

    const folder = await createRes.json();
    return folder.id;
  } catch (err) {
    console.error('Error in getOrCreateFolder:', err);
    throw err;
  }
};

// Upload generated invoice to Google Drive
export const uploadInvoicePDF = async (
  accessToken: string,
  filename: string,
  pdfBlob: Blob,
  folderId?: string
): Promise<{ id: string; name: string; webViewLink?: string }> => {
  try {
    const metadata = {
      name: filename,
      mimeType: 'application/pdf',
      parents: folderId ? [folderId] : undefined
    };

    const boundary = 'cloudairambo_boundary_multipart';
    const textEncoder = new TextEncoder();

    const header =
      `\r\n--${boundary}\r\n` +
      'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
      JSON.stringify(metadata) +
      `\r\n--${boundary}\r\n` +
      'Content-Type: application/pdf\r\n\r\n';

    const footer = `\r\n--${boundary}--`;

    const headerBytes = textEncoder.encode(header);
    const footerBytes = textEncoder.encode(footer);
    const fileBytes = new Uint8Array(await pdfBlob.arrayBuffer());

    const totalLength = headerBytes.length + fileBytes.length + footerBytes.length;
    const body = new Uint8Array(totalLength);
    body.set(headerBytes, 0);
    body.set(fileBytes, headerBytes.length);
    body.set(footerBytes, headerBytes.length + fileBytes.length);

    const response = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': `multipart/related; boundary=${boundary}`
        },
        body: body
      }
    );

    if (!response.ok) {
      throw new Error(`Google Drive upload failed: ${await response.text()}`);
    }

    return await response.json();
  } catch (err) {
    console.error('uploadInvoicePDF error:', err);
    throw err;
  }
};

// Upload generated excel spreadsheet to Google Drive
export const uploadExcelToDrive = async (
  accessToken: string,
  filename: string,
  excelBlob: Blob,
  folderId?: string
): Promise<{ id: string; name: string; webViewLink?: string }> => {
  try {
    const metadata = {
      name: filename,
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      parents: folderId ? [folderId] : undefined
    };

    const boundary = 'cloudairambo_boundary_multipart';
    const textEncoder = new TextEncoder();

    const header =
      `\r\n--${boundary}\r\n` +
      'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
      JSON.stringify(metadata) +
      `\r\n--${boundary}\r\n` +
      'Content-Type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet\r\n\r\n';

    const footer = `\r\n--${boundary}--`;

    const headerBytes = textEncoder.encode(header);
    const footerBytes = textEncoder.encode(footer);
    const fileBytes = new Uint8Array(await excelBlob.arrayBuffer());

    const totalLength = headerBytes.length + fileBytes.length + footerBytes.length;
    const body = new Uint8Array(totalLength);
    body.set(headerBytes, 0);
    body.set(fileBytes, headerBytes.length);
    body.set(footerBytes, headerBytes.length + fileBytes.length);

    const response = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': `multipart/related; boundary=${boundary}`
        },
        body: body
      }
    );

    if (!response.ok) {
      throw new Error(`Google Drive excel upload failed: ${await response.text()}`);
    }

    return await response.json();
  } catch (err) {
    console.error('uploadExcelToDrive error:', err);
    throw err;
  }
};

// List files under the database folder in Drive
export const listUploadedInvoices = async (accessToken: string, folderId: string): Promise<any[]> => {
  try {
    const q = `'${folderId}' in parents and trashed=false`;
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&orderBy=createdTime desc&fields=files(id,name,webViewLink,createdTime,size)`,
      {
        headers: { Authorization: `Bearer ${accessToken}` }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to list invoices: ${await response.text()}`);
    }

    const data = await response.json();
    return data.files || [];
  } catch (err) {
    console.error('Error listing invoices:', err);
    return [];
  }
};
